* {
  box-sizing: border-box;
}

:root {
  color: #17211c;
  background: #f7f7f2;
  font-family:
    Inter,
    ui-sans-serif,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  font-synthesis: none;
  line-height: 1.45;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  --ink: #17211c;
  --muted: #66736b;
  --paper: #fffdf7;
  --line: #d8ddd1;
  --accent: #126b5c;
  --accent-2: #b6422f;
  --gold: #b7801f;
  --blue: #315f9f;
  --shadow: 0 14px 42px rgba(23, 33, 28, 0.12);
}

body {
  margin: 0;
  min-width: 320px;
}

button,
input,
select,
textarea {
  font: inherit;
}

button {
  border: 0;
}

button:focus-visible,
a:focus-visible,
input:focus-visible,
select:focus-visible,
textarea:focus-visible {
  outline: 3px solid rgba(18, 107, 92, 0.38);
  outline-offset: 2px;
}

#root {
  min-height: 100svh;
}

.app-shell {
  min-height: 100svh;
  display: grid;
  grid-template-rows: auto 1fr;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 14px clamp(18px, 4vw, 48px);
  border-bottom: 1px solid var(--line);
  background: rgba(255, 253, 247, 0.92);
  backdrop-filter: blur(16px);
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 220px;
}

.brand-mark {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 8px;
  color: #fffdf7;
  background: var(--accent);
}

.brand strong {
  display: block;
  color: var(--ink);
  font-size: 17px;
}

.brand span,
.topbar-meta,
.panel-eyebrow,
.tool-meta,
.status-line,
.microcopy {
  color: var(--muted);
  font-size: 13px;
}

.topbar-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 4px 9px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #ffffff;
  color: var(--ink);
  font-size: 12px;
}

.workspace {
  display: grid;
  grid-template-columns: minmax(260px, 336px) 1fr;
  min-height: 0;
}

.sidebar {
  padding: 24px;
  border-right: 1px solid var(--line);
  background: #eeefe7;
}

.intro {
  margin-bottom: 20px;
}

.intro h1 {
  margin: 0 0 8px;
  color: var(--ink);
  font-size: clamp(30px, 5vw, 52px);
  line-height: 1;
}

.intro p {
  margin: 0;
  color: var(--muted);
}

.tool-list {
  display: grid;
  gap: 8px;
}

.tool-button {
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 10px;
  width: 100%;
  padding: 11px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}

.tool-button:hover,
.tool-button.active {
  border-color: #c7d1c5;
  background: #fffdf7;
  box-shadow: 0 8px 24px rgba(23, 33, 28, 0.08);
}

.tool-icon {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 7px;
  color: #fff;
  background: var(--accent);
}

.tool-title {
  display: block;
  font-size: 14px;
  font-weight: 750;
}

.tool-engine {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.lab-stage {
  min-width: 0;
  padding: clamp(18px, 3vw, 36px);
}

.hero-band {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: clamp(20px, 4vw, 52px);
  align-items: stretch;
  margin-bottom: 24px;
}

.hero-copy {
  display: flex;
  min-height: 300px;
  flex-direction: column;
  justify-content: center;
  padding: clamp(24px, 5vw, 58px) 0;
}

.hero-copy h2 {
  max-width: 760px;
  margin: 0 0 14px;
  color: var(--ink);
  font-size: clamp(32px, 6vw, 70px);
  line-height: 0.96;
}

.hero-copy p {
  max-width: 660px;
  margin: 0;
  color: var(--muted);
  font-size: 18px;
}

.lab-visual {
  position: relative;
  overflow: hidden;
  min-height: 320px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background:
    linear-gradient(90deg, rgba(18, 107, 92, 0.13) 1px, transparent 1px),
    linear-gradient(rgba(18, 107, 92, 0.13) 1px, transparent 1px), #faf7ed;
  background-size: 30px 30px;
  box-shadow: var(--shadow);
}

.lab-visual svg {
  width: 100%;
  height: 100%;
}

.tool-header {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.tool-header h2 {
  margin: 0 0 6px;
  color: var(--ink);
  font-size: clamp(26px, 4vw, 44px);
  line-height: 1.03;
}

.tool-header p {
  max-width: 720px;
  margin: 0;
  color: var(--muted);
}

.tool-grid {
  display: grid;
  grid-template-columns: minmax(270px, 360px) 1fr;
  gap: 20px;
  align-items: start;
}

.control-panel,
.output-panel,
.lesson-panel {
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
  box-shadow: 0 8px 26px rgba(23, 33, 28, 0.08);
}

.control-panel,
.lesson-panel {
  padding: 18px;
}

.output-panel {
  overflow: hidden;
}

.panel-title {
  margin: 0 0 14px;
  color: var(--ink);
  font-size: 18px;
}

.control-stack {
  display: grid;
  gap: 15px;
}

.field {
  display: grid;
  gap: 6px;
}

.field label {
  color: var(--ink);
  font-size: 13px;
  font-weight: 700;
}

.field input,
.field select,
.field textarea {
  width: 100%;
  min-height: 38px;
  padding: 8px 10px;
  border: 1px solid #cbd3c5;
  border-radius: 7px;
  background: #fff;
  color: var(--ink);
}

.field input[type="range"] {
  padding: 0;
}

.button-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.command {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 8px 12px;
  border-radius: 7px;
  background: var(--accent);
  color: #fff;
  cursor: pointer;
  font-weight: 750;
}

.command.secondary {
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
}

.command.warning {
  background: var(--accent-2);
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  border-bottom: 1px solid var(--line);
  background: var(--line);
}

.metric {
  padding: 14px;
  background: #fff;
}

.metric strong {
  display: block;
  color: var(--ink);
  font-size: 20px;
}

.metric span {
  color: var(--muted);
  font-size: 12px;
}

.plot {
  display: block;
  width: 100%;
  height: 360px;
  background: #fbfbf7;
}

.lesson-panel {
  margin-top: 20px;
}

.lesson-panel ol {
  margin: 10px 0 0;
  padding-left: 22px;
  color: var(--muted);
}

.toast {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 30;
  max-width: min(420px, calc(100vw - 36px));
  padding: 12px 14px;
  border: 1px solid #cfe1ce;
  border-radius: 8px;
  background: #fff;
  box-shadow: var(--shadow);
  color: var(--ink);
}

.error-box {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #e3b3a9;
  border-radius: 7px;
  background: #fff4f1;
  color: #803424;
}

@media (max-width: 980px) {
  .workspace,
  .hero-band,
  .tool-grid {
    grid-template-columns: 1fr;
  }

  .sidebar {
    border-right: 0;
    border-bottom: 1px solid var(--line);
  }

  .tool-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .topbar,
  .tool-header {
    align-items: stretch;
    flex-direction: column;
  }

  .tool-list,
  .results-grid {
    grid-template-columns: 1fr;
  }

  .plot {
    height: 280px;
  }
}
